import React, { useEffect, useState } from "react"
import * as Yup from "yup"
import {
  Col,
  Container,
  Row,
  Label,
  Form,
  Input,
  FormFeedback,
  Button,
  Card,
  CardBody,
} from "reactstrap"
import "flatpickr/dist/themes/material_blue.css"
import FlatPickr from "react-flatpickr"

import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { useFormik } from "formik"
import { Link, useNavigate } from "react-router-dom"
import calendar from "../../../assets/images/icons/calendar.svg"
import IcdProcess from "../../../assets/images/icd-process.png"

import SuccessRequestModal from "./successRequestModal"
import Spinners from "components/Common/Spinner"
import { useCreateFundRequestMutation } from "store/api/investorapi/icds.api"
import toast from "react-hot-toast"
import { interestFrequency, repaymentSchedule } from "helpers/utils"
import { useSelector } from "react-redux"
import { formatAmount, sanitizeIntegerInput } from "helpers/common"
import SignAgreementModal from "../signAgreementModal"
import moment from "moment"

const defaultValues = {
  amount: "",
  entity_name: "",
  interest_rate: "",
  tenure: "",
  repayment_date: "",
  fund_requirement_date: "",
  interest_frequency: "",
  repayment_schedule: "SINGLE",
  repayment_terms: "",
}
const minDate = new Date()
minDate.setHours(0, 0, 0, 0)
minDate.setDate(minDate.getDate() + 7)

// const maxDate = new Date()
// maxDate.setDate(maxDate.getDate() + 365)
// minDate.setHours(0, 0, 0, 0)
// maxDate.setHours(23, 59, 59, 999)

const CreateICDInvestments = () => {
  const navigate = useNavigate()
  const [onCreate, { isLoading }] = useCreateFundRequestMutation()
  const { user, userType } = useSelector((state) => state.user)

  const [open, setOpen] = useState(false)
  const [cancelRequestOpen, setCancelRequestOpen] = useState(false)

  //meta title
  document.title = "Fund Availability Request - Prachay"

  const onFormSubmit = async (values) => {
    try {
      const { message, success } = await onCreate(values).unwrap()
      if (success) {
        toast.success(message, { id: "toasterMessage" })
        navigate("/icd-investments/my-icds")
      }
    } catch (error) {
      if (error.status === 409) {
        setCancelRequestOpen(true)
      }

      const data = error?.data
      if (data?.errors) {
        const { errors } = data
        validation.setErrors(errors)
      }
      toast.error(error?.data?.message, { id: "toasterMessage" })
    }
  }

  const validation = useFormik({
    initialValues: { ...defaultValues },
    validationSchema: Yup.object({}),

    onSubmit: (values) => {
      onFormSubmit(values)
    },
  })

  useEffect(() => {
    const { repayment_date, fund_requirement_date } = validation.values

    if (repayment_date && fund_requirement_date) {
      const repayment = moment(repayment_date).startOf("day")
      const fundReq = moment(fund_requirement_date).startOf("day")

      const tenureInDays = repayment.diff(fundReq, "days")

      validation.setFieldValue("tenure", `${tenureInDays}`)
    }
  }, [
    validation.values.repayment_date,
    validation.values.fund_requirement_date,
  ])

  useEffect(() => {
    if (user && user?.entity_type === "COMPANY") {
      validation.setFieldValue("entity_name", user.name)
    }
  }, [user])

  const checkDisabled = () => {
    if (user && user?.entity_type === "COMPANY") {
      return true
    }
    return false
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Render Breadcrumb */}
          <Breadcrumbs title={"Create ICD Request"} />

          <Row>
            <Col xl={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-center">
                    <img
                      src={IcdProcess}
                      className="img-fluid mb-2"
                      style={{
                        maxHeight: "65px",
                      }}
                      alt=""
                    />
                  </div>
                  <Form
                    className="form-horizontal"
                    onSubmit={(e) => {
                      e.preventDefault()
                      validation.handleSubmit()
                      return false
                    }}
                  >
                    <Row className="mb-2">
                      <Col xl={6}>
                        <Label for="entity_name" className="col-form-label">
                          Name of the Entity <span className="error">*</span>
                        </Label>
                        <Input
                          name="entity_name"
                          type="text"
                          disabled={checkDisabled()}
                          className="form-control"
                          placeholder="Entity Name"
                          value={validation.values.entity_name}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={
                            validation.touched.entity_name &&
                            validation.errors.entity_name
                              ? true
                              : false
                          }
                        />
                        {validation.errors.entity_name &&
                        validation.touched.entity_name ? (
                          <FormFeedback type="invalid">
                            {validation.errors.entity_name}
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col xl={6}>
                        <Label for="amount" className="col-form-label">
                          Investment Amount <span className="error">*</span>
                        </Label>
                        <Input
                          name="amount"
                          type="text"
                          className="form-control"
                          id="amount"
                          placeholder="Investment Amount"
                          value={`₹ ${formatAmount(validation.values.amount)}`}
                          onChange={(e) =>
                            sanitizeIntegerInput(
                              e,
                              validation.setFieldValue,
                              "amount"
                            )
                          }
                          onBlur={validation.handleBlur}
                          invalid={
                            validation.touched.amount &&
                            validation.errors.amount
                              ? true
                              : false
                          }
                        />
                        {validation.errors.amount &&
                        validation.touched.amount ? (
                          <FormFeedback type="invalid">
                            {validation.errors.amount}
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col xl={6}>
                        <Label for="amount" className="col-form-label">
                          Date of Repayment <span className="error">*</span>
                        </Label>

                        <FlatPickr
                          id="repayment_date"
                          name="repayment_date"
                          className={`form-control ${
                            validation.touched.repayment_date &&
                            validation.errors.repayment_date &&
                            "is-invalid"
                          }`}
                          placeholder="Select date"
                          options={{
                            dateFormat: "d M, Y",
                            minDate,
                            // maxDate,
                          }}
                          value={validation.values.repayment_date}
                          onChange={(date) => {
                            const newRepaymentDate = date[0]
                            validation.setFieldValue(
                              "repayment_date",
                              newRepaymentDate
                            )
                            validation.setFieldValue(
                              "fund_requirement_date",
                              ""
                            )
                          }}
                        />
                        {validation.errors.repayment_date &&
                        validation.touched.repayment_date ? (
                          <FormFeedback type="invalid">
                            {validation.errors.repayment_date}
                          </FormFeedback>
                        ) : null}
                      </Col>

                      <Col xl={6}>
                        <Label
                          // htmlFor="fund_requirement_date"
                          className="col-form-label"
                        >
                          Fund Availability Date{" "}
                          <span className="error">*</span>
                        </Label>
                        <div className="input-wrapper">
                          <FlatPickr
                            id="fund_requirement_date"
                            name="fund_requirement_date"
                            className={`form-control ${
                              validation.touched.fund_requirement_date &&
                              validation.errors.fund_requirement_date &&
                              "is-invalid"
                            }`}
                            placeholder="Select date"
                            options={{
                              dateFormat: "d M, Y",
                              minDate: "today",
                              maxDate: validation.values.repayment_date
                                ? moment(validation.values.repayment_date)
                                    .subtract(7, "days")
                                    .toDate()
                                : undefined,
                            }}
                            disabled={!validation.values.repayment_date}
                            selected={validation.values.fund_requirement_date}
                            value={validation.values.fund_requirement_date}
                            onChange={(date) => {
                              validation.setFieldValue(
                                "fund_requirement_date",
                                date[0]
                              )
                            }}
                          />
                          <img src={calendar} alt="" />
                        </div>

                        {validation.errors.fund_requirement_date &&
                        validation.touched.fund_requirement_date ? (
                          <FormFeedback type="invalid">
                            {validation.errors.fund_requirement_date}
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col xl={6}>
                        <Label for="tenure" className="col-form-label">
                          Tenure (Days) <span className="error">*</span>
                        </Label>
                        <Input
                          disabled
                          name="tenure"
                          type="text"
                          className="form-control"
                          id="tenure"
                          min={1}
                          placeholder="Tenure"
                          value={validation.values.tenure}
                          onChange={(e) =>
                            sanitizeIntegerInput(
                              e,
                              validation.setFieldValue,
                              "tenure"
                            )
                          }
                          invalid={
                            validation.touched.tenure &&
                            validation.errors.tenure
                              ? true
                              : false
                          }
                        />
                        {validation.errors.tenure &&
                        validation.touched.tenure ? (
                          <FormFeedback type="invalid">
                            {validation.errors.tenure}
                          </FormFeedback>
                        ) : null}
                        <span
                          className="text-muted"
                          style={{
                            fontStyle: "italic",
                          }}
                        >
                          (Tenure = Date of Repayment - Fund Availability Date)
                        </span>
                      </Col>

                      <Col xl={6}>
                        <Label for="interest_rate" className="col-form-label">
                          Interest Rate (%) <span className="error">*</span>
                        </Label>
                        <Input
                          name="interest_rate"
                          type="text"
                          className="form-control"
                          id="interest_rate"
                          placeholder="Interest Rate (%)"
                          value={validation.values.interest_rate}
                          onChange={(e) =>
                            sanitizeIntegerInput(
                              e,
                              validation.setFieldValue,
                              "interest_rate"
                            )
                          }
                          invalid={
                            validation.touched.interest_rate &&
                            validation.errors.interest_rate
                              ? true
                              : false
                          }
                        />
                        {validation.errors.interest_rate &&
                        validation.touched.interest_rate ? (
                          <FormFeedback type="invalid">
                            {validation.errors.interest_rate}
                          </FormFeedback>
                        ) : null}
                      </Col>

                      <Col xl={6}>
                        <Label className="col-form-label">
                          Interest Payment Frequency
                          <span className="error">*</span>
                        </Label>

                        <select
                          id="interest_frequency"
                          name="interest_frequency"
                          className={`form-select pageSize ${
                            validation.touched.interest_frequency &&
                            validation.errors.interest_frequency &&
                            "is-invalid"
                          }`}
                          value={validation.values.interest_frequency}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                        >
                          <option value="">
                            {" "}
                            Select Interest Payment Frequency
                          </option>
                          {interestFrequency.map((item, index) => (
                            <option
                              key={`interestFrequency_${index}`}
                              value={item.key}
                            >
                              {item.label}
                            </option>
                          ))}
                        </select>
                        {validation.errors.interest_frequency &&
                        validation.touched.interest_frequency ? (
                          <FormFeedback type="invalid">
                            {validation.errors.interest_frequency}
                          </FormFeedback>
                        ) : null}
                      </Col>

                      <Col xl={6}>
                        <Label className="col-form-label">
                          Repayment Schedule<span className="error">*</span>
                        </Label>

                        <select
                          id="repayment_schedule"
                          name="repayment_schedule"
                          className={`form-select pageSize ${
                            validation.touched.repayment_schedule &&
                            validation.errors.repayment_schedule &&
                            "is-invalid"
                          }`}
                          value={validation.values.repayment_schedule}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                        >
                          <option value=""> Select Repayment Schedule</option>
                          {repaymentSchedule.map((item, index) => (
                            <option
                              key={`interestFrequency_${index}`}
                              value={item.key}
                            >
                              {item.label}
                            </option>
                          ))}
                        </select>
                        {validation.errors.repayment_schedule &&
                        validation.touched.repayment_schedule ? (
                          <FormFeedback type="invalid">
                            {validation.errors.repayment_schedule}
                          </FormFeedback>
                        ) : null}
                      </Col>

                      {validation.values.repayment_schedule === "MULTIPLE" && (
                        <Col xl={6}>
                          <Label className="col-form-label">
                            No. Of Installments <span className="error">*</span>
                          </Label>
                          <Input
                            name="repayment_terms"
                            type="text"
                            className="form-control"
                            id="repayment_terms"
                            placeholder="Total Installments"
                            value={validation.values.repayment_terms}
                            onChange={(e) =>
                              sanitizeIntegerInput(
                                e,
                                validation.setFieldValue,
                                "repayment_terms"
                              )
                            }
                            invalid={
                              validation.touched.repayment_terms &&
                              validation.errors.repayment_terms
                                ? true
                                : false
                            }
                          />
                          {validation.errors.repayment_terms &&
                          validation.touched.repayment_terms ? (
                            <FormFeedback type="invalid">
                              {validation.errors.repayment_terms}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      )}
                    </Row>
                    <Row className="justify-content-end mt-4 pb-4">
                      <Col sm={12}>
                        <div className="d-flex justify-content-end align-items-center gap-3">
                          <Link to={"/icd-investments/my-icds"}>
                            <button
                              type="button"
                              className="btn btn-cancel w-md"
                            >
                              Cancel
                            </button>
                          </Link>
                          <Button
                            type="submit"
                            disabled={isLoading}
                            className="btn btn-primary w-md"
                          >
                            {isLoading ? <Spinners /> : "Submit Request"}
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* <Row>
            <Col xl={12}>
              <CardTitle className="mb-4">My Fund Requests</CardTitle>
            </Col>
          </Row> */}
        </Container>
      </div>
      <SuccessRequestModal open={open} setOpen={setOpen} />
      <SignAgreementModal
        open={cancelRequestOpen}
        setOpen={setCancelRequestOpen}
        showAlert={true}
      />
    </React.Fragment>
  )
}

export default CreateICDInvestments
